/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './UploadFilesDropArea.style';
import { Button, List } from 'antd';
import { useIntl } from 'react-intl';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  dropTargetForExternal,
  monitorForExternal,
} from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import {
  containsFiles,
  getFiles,
} from '@atlaskit/pragmatic-drag-and-drop/external/file';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { bind } from 'bind-event-listener';
import IconButton from '~components/_common/IconButton/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

//Move to separate utils
const isImageFile = (file: UserUpload | File) => {
  if (file.type.startsWith('image')) return true;
  return false;
};

type UserUpload = {
  type: string;
  dataUrl: string;
  name: string;
  size: number;
  id: string;
};

const Upload = memo(function Upload({ upload }: { upload: UserUpload }) {
  const styles = useStyles(styleFn);

  const [state, setState] = useState<'loading' | 'ready'>('loading');
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const clearTimeout = useRef<() => void>(() => {});

  useEffect(function mount() {
    return function unmount() {
      clearTimeout.current();
    };
  }, []);

  return (
    <div
      css={[
        styles.upload,
        state === 'loading' ? styles.loading : styles.ready,
      ]}>
      <img
        src={upload.dataUrl}
        css={styles.imageStyles}
        onLoad={() => {
          // this is the _only_ way I could find to get the animation to run
          // correctly every time in all browsers
          // setTimeout(fn, 0) -> sometimes wouldn't work in chrome (event nesting two)
          // requestAnimationFrame -> nope (event nesting two)
          // requestIdleCallback -> nope (doesn't work in safari)
          // I can find no reliable hook for applying the `ready` state,
          const timerId = setTimeout(() => setState('ready'), 100);
          clearTimeout.current = () => window.clearTimeout(timerId);
        }}
        alt={upload.name}
      />
      <div css={styles.uploadDetailStyles}>
        <em css={styles.uploadFilenameStyles}>{upload.name}</em>
        <code>{Math.round(upload.size / 1000)}kB</code>
      </div>
    </div>
  );
});

const Gallery = memo(function Gallery({ uploads }: { uploads: UserUpload[] }) {
  const styles = useStyles(styleFn);

  if (!uploads.length) {
    return null;
  }

  return (
    <div css={styles.galleryStyles}>
      {uploads.map((upload, index) => (
        <Upload upload={upload} key={index} />
      ))}
    </div>
  );
});

const FileItem = memo(function FileItem({
  upload,
  onFileDelete,
}: {
  upload: UserUpload;
  onFileDelete: (id: string) => void;
}) {
  const styles = useStyles(styleFn);
  const [imagePreviewUrl, setImagePreview] = useState<
    string | ArrayBuffer | null
  >(null);

  const hasPreview = isImageFile(upload);
  // console.log('FileItem upload', upload);
  const handleImagePreview = () => {
    if (upload) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Set the image source to the data URL
        setImagePreview(reader.result);
      };
      reader.readAsDataURL((imagePreviewUrl as unknown) as Blob);
    }
  };

  return (
    <div css={styles.fileItemContainer}>
      <FontAwesomeIcon icon={['far', 'file']} />
      <div css={styles.fileItemDetail}>
        <span css={styles.fileItemName}>{upload.name}</span>
      </div>
      <div css={styles.buttonsBlock}>
        <code>{Math.round(upload.size / 1000)}kB</code>
        {hasPreview && (
          <IconButton click={handleImagePreview} icon={['far', 'eye']} />
        )}
        <IconButton
          click={() => onFileDelete(upload.id)}
          icon={['far', 'trash']}
        />
      </div>
    </div>
  );
});

const FilesList = memo(function FilesList({
  uploads,
  onFileDelete,
}: {
  uploads: UserUpload[];
  onFileDelete: (id: string) => void;
}) {
  const styles = useStyles(styleFn);

  return (
    <div css={styles.filesListContainer}>
      <List
        dataSource={uploads}
        renderItem={(upload) => (
          <FileItem onFileDelete={onFileDelete} upload={upload} />
        )}
      />
    </div>
  );
});

export const UploadFilesDropArea = () => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<'idle' | 'potential' | 'over'>('idle');
  const [uploads, setUploads] = useState<UserUpload[]>([]);

  /**
   * Creating a stable reference so that we can use it in our unmount effect.
   *
   * If we used uploads as a dependency in the second `useEffect` it would run
   * every time the uploads changed, which is not desirable.
   */
  const stableUploadsRef = useRef<UserUpload[]>(uploads);
  useEffect(() => {
    stableUploadsRef.current = uploads;
  }, [uploads]);

  useEffect(() => {
    return () => {
      /**
       * MDN recommends explicitly releasing the object URLs when possible,
       * instead of relying just on the browser's garbage collection.
       */
      stableUploadsRef.current.forEach((upload) => {
        URL.revokeObjectURL(upload.dataUrl);
      });
    };
  }, []);

  const addUpload = useCallback((file: File | null) => {
    if (!file) {
      return;
    }

    // if (!isImageFile(file)) {
    //   return;
    // }

    const upload: UserUpload = {
      id: `${Date.now()}-${Math.random()}`,
      type: file.type,
      dataUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    };
    // console.log('upload', upload);
    setUploads((current) => [...current, upload]);
  }, []);

  const onFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.currentTarget.files ?? []);
      files.forEach(addUpload);
    },
    [addUpload]
  );

  const handleFileDelete = (id: string) => {
    setUploads((current) => current.filter((upload) => upload.id !== id));
  };

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    return combine(
      dropTargetForExternal({
        element: el,
        canDrop: containsFiles,
        onDragEnter: () => setState('over'),
        onDragLeave: () => setState('potential'),
        onDrop: async ({ source }) => {
          const files = await getFiles({ source });

          files.forEach((file) => {
            if (file == null) {
              return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);

            // for simplicity:
            // - not handling errors
            // - not aborting the
            // - not unbinding the event listener when the effect is removed
            bind(reader, {
              type: 'load',
              listener(event) {
                const result = reader.result;
                if (typeof result === 'string') {
                  const upload: UserUpload = {
                    id: `${Date.now()}-${Math.random()}`,
                    type: file.type,
                    dataUrl: result,
                    name: file.name,
                    size: file.size,
                  };
                  setUploads((current) => [...current, upload]);
                }
              },
            });
          });
        },
      }),
      monitorForExternal({
        canMonitor: containsFiles,
        onDragStart: () => {
          setState('potential');
          preventUnhandled.start();
        },
        onDrop: () => {
          setState('idle');
          preventUnhandled.stop();
        },
      })
    );
  });

  /**
   * We trigger the file input manually when clicking the button. This also
   * works when selecting the button using a keyboard.
   *
   * We do this for two reasons:
   *
   * 1. Styling file inputs is very limited.
   * 2. Associating the button as a label for the input only gives us pointer
   *    support, but does not work for keyboard.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const onInputTriggerClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div css={styles.uploadFilesContainer}>
      <div css={styles.uploadBtnContainer}>
        <Button onClick={onInputTriggerClick}>
          <FontAwesomeIcon
            css={styles.uploadIcon}
            icon={['far', 'file-upload']}
          />
          {intl.formatMessage({ id: 'upload-files' })}
        </Button>
      </div>

      <div
        ref={ref}
        css={[
          styles.fileContainer,
          state === 'over'
            ? styles.overContainer
            : state === 'potential'
            ? styles.potentialContainer
            : undefined,
        ]}>
        {uploads.length === 0 ? (
          <strong css={styles.textStyles}>
            {intl.formatMessage({ id: 'drag-drop-files-here' })}
          </strong>
        ) : (
          <FilesList uploads={uploads} onFileDelete={handleFileDelete} />
        )}

        <input
          ref={inputRef}
          css={styles.displayNoneStyles}
          id="file-input"
          onChange={onFileInputChange}
          type="file"
          // accept="image/*"
          multiple
        />
      </div>
    </div>
  );
};
